import { Order } from '@components/account/types'
import styles from './styles.module.scss'
import { useRouter } from 'next/router'
import useSWR from 'swr'
import { OrderItemHeader } from '../OrderItem/OrderItemHeader'
import { getOrderStatus } from '../helpers/getOrderStatus'
import { CustomOrder, transformOrder } from '../helpers/transformOrder'
import currencyConverter from '@helpers/misc/currencyConverter'
import { OrderPageContent } from './OrderPageContent'
import { NoOrderItem } from '../NoOrderItem'
import { makeCustomerRequest } from '@api/graphqlShopify/makeCustomerRequest'
import customerAccountOrderQuery from '@api/graphqlShopify/queries/customerAccountOrderQuery'
import { useAuth } from '@api/accounts/auth/hooks/useAuth'
import trackError from '@helpers/trackError'
import OrderPageSkeleton from '../shared/SkeletonUI/OrderPageSkeleton'

async function getOrderById(id: string, token: string) {
  const { data } = await makeCustomerRequest<{
    order: Order
  }>(
    customerAccountOrderQuery,
    {
      id: `gid://shopify/Order/${id}`
    },
    token
  )
  const order = transformOrder(data.order)

  return order as CustomOrder
}

export default function OrderPage() {
  const router = useRouter()
  const { orderId } = router.query
  const { token, makeRequestWithRefreshCheck } = useAuth()
  const { data, error, isLoading } = useSWR(
    orderId && token ? [orderId, token] : null,
    ([orderId]) => makeRequestWithRefreshCheck(getOrderById, orderId as string),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      onError: error => {
        console.log('Failed to retrieve order')
        trackError(new Error('Failed to retrieve order'), {
          orderId,
          error,
          section: 'Account Page'
        })
      }
    }
  )

  if (isLoading) {
    return (
      <div className={styles.OrderPage}>
        <OrderPageSkeleton />
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.OrderPage}>
        <NoOrderItem variant="error" />
      </div>
    )
  }

  if (!data) {
    return <NoOrderItem />
  }

  const {
    fulfillments,
    lineItems,
    cancelledAt,
    cancelReason,
    refunds
  } = data

  const { status, label } = getOrderStatus(
    fulfillments,
    lineItems,
    cancelledAt,
    refunds,
    cancelReason
  )
  const orderSummary = `${data.lineItems.length} item${
    data.lineItems.length > 1 ? 's' : ''
  } | Order total: ${currencyConverter({
    price: data.totalPrice.amount,
    currency: data.totalPrice.currencyCode
  })}`

  return (
    <div className={styles.OrderPage}>
      <OrderItemHeader name={data.name} status={status} label={label}>
        {orderSummary}
      </OrderItemHeader>
      <OrderPageContent
        order={data}
        orderSummary={orderSummary}
        status={status}
      />
    </div>
  )
}
